import React, { FC, ReactNode, useEffect, useRef } from 'react';
import Constants from '~/app/Constants';
import { useNavigate } from 'react-router-dom';
import { redirectTo } from 'omni-package-react-core';
import AppLoadingIcon from '~/components/AppLoadingIcon/AppLoadingIcon';
import { getCompanyByPath } from '~/utils/companyUtils';
import { setLocationHref } from '~/utils/browserNavigation';
import { CENTRALIZATION_BYPASS_KEY, CENTRALIZATION_SESSION_BYPASS_KEY } from '~/utils/centralizationConstants';
import { Company } from '~/interfaces/CompanyConfig';
import useAccountSecurityState from './hooks/useAccountSecurityState';
import useRequiredRoutes from './hooks/useRequiredRoutes';
import useSecurityRedirect from './hooks/useSecurityRedirect';

// Regression note (branch vs master baseline): redirect decisions now need to coordinate
// mandatory pages with post-auth/post-confirmation handoff, without losing intended destination.
const shouldBypassSecuredCentralizationRedirect = (
  bypassActive: boolean,
  needsToGoToSecuredRoute: boolean,
  firstRequiredRoutePath: string | undefined,
): boolean => {
  const isCentralizationRoute = Boolean(firstRequiredRoutePath?.includes(Constants.PagesUrl.CentralizationBaseFragment));
  return needsToGoToSecuredRoute && isCentralizationRoute && bypassActive;
};

const matchesRoute = (targetPath: string, route: string): boolean => {
  if (route === Constants.PagesUrl.HomePage) {
    return targetPath === Constants.PagesUrl.HomePage;
  }

  return targetPath === route || targetPath.startsWith(`${route}/`);
};

const isAllowedRoutePath = (targetPath: string): boolean => {
  const protectedMatch = Constants.ProtectedRoutes.some((route) => matchesRoute(targetPath, route));
  if (protectedMatch) {
    return true;
  }

  return Constants.PublicRoutesWithAuthenticatedLayout.some((route) => matchesRoute(targetPath, route));
};

const getStoredRedirectUrl = (): string | undefined => {
  // Accept only same-origin and known app routes to restore user intent safely.
  const storedRedirectUrl = sessionStorage.getItem('redirect-url');
  if (!storedRedirectUrl) {
    return undefined;
  }

  let normalizedStoredRedirect = storedRedirectUrl;

  if (storedRedirectUrl.startsWith('http://') || storedRedirectUrl.startsWith('https://')) {
    const currentOrigin = globalThis.location.origin;
    try {
      const parsedUrl = new URL(storedRedirectUrl);
      if (parsedUrl.origin !== currentOrigin) {
        return undefined;
      }
      normalizedStoredRedirect = `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch {
      return undefined;
    }
  }

  const targetPath = normalizedStoredRedirect.split('?')[0];
  const isAllowedPath = isAllowedRoutePath(targetPath);

  return isAllowedPath ? normalizedStoredRedirect : undefined;
};

const getBypassState = (): { bypassUntil: number; bypassActive: boolean; sessionBypassActive: boolean } => {
  const bypassUntilRaw = sessionStorage.getItem(CENTRALIZATION_BYPASS_KEY);
  const bypassUntil = bypassUntilRaw ? Number(bypassUntilRaw) : 0;
  const timedBypassActive = Number.isFinite(bypassUntil) && bypassUntil > Date.now();
  const sessionBypassActive = sessionStorage.getItem(CENTRALIZATION_SESSION_BYPASS_KEY) === 'true';
  const bypassActive = timedBypassActive || sessionBypassActive;

  return { bypassUntil, bypassActive, sessionBypassActive };
};

export interface AccountSecurityValidatorProps {
  children: ReactNode;
}

const AccountSecurityValidator: FC<AccountSecurityValidatorProps> = ({ children }) => {
  const navigate = useNavigate();
  const state = useAccountSecurityState();
  const lastRedirectKeyRef = useRef<string>('');

  const redirectIfNeeded = (redirectKey: string, callback: () => void): void => {
    // Prevent redirect loops / duplicate redirects during state churn.
    if (lastRedirectKeyRef.current === redirectKey) {
      return;
    }
    lastRedirectKeyRef.current = redirectKey;
    callback();
  };

  const { currentRequiredRoute, firstRequiredRoute } = useRequiredRoutes({
    isUsernameValidationRequired: state.isUsernameValidationRequired,
    isSecurityInfoValidationRequired: state.isSecurityInfoValidationRequired,
    mfaOnboardingStatus: state.mfaOnboardingStatus,
    isMfaOnboardingLoading: state.isMfaOnboardingLoading,
    isCentralizationRequired: state.isCentralizationRequired,
    isCentralizationOnboardingLoading: state.isCentralizationOnboardingLoading,
    currentPath: state.currentPath,
  });

  const redirect = useSecurityRedirect({
    hasOnboardingError: state.hasOnboardingError,
    currentPath: state.currentPath,
    currentRequiredRoute,
    firstRequiredRoute,
  });

  const { bypassUntil, bypassActive, sessionBypassActive } = getBypassState();
  const shouldBypassCentralizationRedirect = shouldBypassSecuredCentralizationRedirect(
    bypassActive,
    redirect.needsToGoToSecuredRoute,
    redirect.firstRequiredRoute?.path,
  );

  const isLoading = state.isAccountLoading || state.isMfaOnboardingLoading || state.isCentralizationOnboardingLoading;
  const isRedirecting = redirect.needsToGoHome
    || (!shouldBypassCentralizationRedirect && redirect.needsToGoToSecuredRoute)
    || redirect.needsToGoToCentralizationError;

  useEffect(() => {
    // Wait for onboarding state before redirecting so an incomplete response cannot force centralization.
    if (isLoading) {
      return;
    }

    if (shouldBypassCentralizationRedirect) {
      return;
    }

    if (redirect.needsToGoToCentralizationError) {
      const redirectKey = `centralization-error:${state.currentPath}->${Constants.PagesUrl.CentralizationErrorPage}`;
      redirectIfNeeded(redirectKey, () => {
        redirectTo(Constants.PagesUrl.CentralizationErrorPage, navigate, undefined, { replace: true });
      });
    } else if (redirect.needsToGoHome) {
      const redirectKey = `home:${state.currentPath}->${redirect.needsToGoHome}`;
      redirectIfNeeded(redirectKey, () => {
        const storedRedirectUrl = getStoredRedirectUrl();
        sessionStorage.removeItem('redirect-url');
        // If we previously captured an allowed destination, restore it before defaulting to home.
        if (storedRedirectUrl && storedRedirectUrl !== state.currentPath) {
          redirectTo(storedRedirectUrl, navigate, undefined, { replace: true });
          return;
        }

        if (getCompanyByPath(state.currentPath) === Company.DEFAULT) {
          redirectTo(Constants.PagesUrl.HomePage, navigate, undefined, { replace: true });
        } else {
          setLocationHref(redirect.needsToGoHome);
        }
      });
    } else if (redirect.needsToGoToSecuredRoute) {
      const redirectKey = `secured-route:${state.currentPath}->${redirect.firstRequiredRoute.path}`;
      redirectIfNeeded(redirectKey, () => {
        const currentSearch = state.currentSearch || '';
        const isCurrentPathAllowed = isAllowedRoutePath(state.currentPath);
        const isCurrentPathCentralization = state.currentPath.includes(Constants.PagesUrl.CentralizationBaseFragment);
        // Preserve intended route before forcing mandatory pages, except when already inside
        // centralization flow to avoid poisoning redirect-url with transient mandatory routes.
        if (isCurrentPathAllowed && !isCurrentPathCentralization) {
          const targetPathToRestore = `${state.currentPath}${currentSearch}`;
          sessionStorage.setItem('redirect-url', targetPathToRestore);
        }
        redirectTo(redirect.firstRequiredRoute.path, navigate, {}, { replace: true });
      });
    } else {
      if (bypassUntil && !bypassActive && !sessionBypassActive) {
        sessionStorage.removeItem(CENTRALIZATION_BYPASS_KEY);
      }
      lastRedirectKeyRef.current = '';
    }
  }, [bypassActive, bypassUntil, isLoading, navigate, redirect, sessionBypassActive, shouldBypassCentralizationRedirect, state.currentPath, state.currentSearch]);

  if (isLoading || isRedirecting) {
    return (<AppLoadingIcon />);
  }

  return (<>{children}</>);
};

export default AccountSecurityValidator;
